import { cn } from "@/lib/cn";

/** What a compact surface marks with a dot (compact surface standard §7). */
export type StatusTone = "question" | "error" | "unread" | "working";

const TONE_CLASS: Record<StatusTone, string> = {
  question: "bg-warning",
  error: "bg-danger",
  unread: "bg-[var(--color-readable-overlay-secondary)]",
  working:
    "bg-[var(--color-readable-overlay-secondary)] motion-safe:animate-pulse",
};

export interface StatusDotProps {
  tone: StatusTone;
  /** Accessible name; without it the dot is aria-hidden. */
  label?: string;
  className?: string;
}

/**
 * StatusDot — the one 7px status dot of the compact surfaces (search hub,
 * Settings pocket, toasts, tray rows, cursor bubble). Yellow: something waits
 * for your answer or action. Red: something went wrong. Soft white: new, or
 * in progress (pulsing, still under reduced motion). Done or read shows no
 * dot: the caller renders nothing.
 */
export function StatusDot({ tone, label, className }: StatusDotProps) {
  return (
    <span
      className={cn(
        "inline-block h-[7px] w-[7px] shrink-0 rounded-full",
        TONE_CLASS[tone],
        className,
      )}
      data-status-tone={tone}
      {...(label
        ? { role: "img", "aria-label": label }
        : { "aria-hidden": true })}
    />
  );
}
